import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from 'react';
import { Loader } from 'lucide-react';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.min.css';
import './../../sass/ipa_style.scss';
import Select from 'react-select';
import ApexCharts from 'apexcharts';
import { TabulatorFull as Tabulator } from "tabulator-tables";
import 'tabulator-tables/dist/css/tabulator_bootstrap5.min.css';
import { CountUp } from 'countup.js';
import { notify, userAccessToken } from '../../helpers';
import { Config } from '../../Config';


/* ================================================================
   UTILITIES
   ================================================================ */

const fmtNum = (n, digits = 3) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  if (Math.abs(num) >= 1000 && Number.isInteger(num)) return num.toLocaleString();
  if (!Number.isInteger(num)) return num.toFixed(digits);
  return num.toLocaleString();
};

const fmtMoney = (n) => {
  if (n == null || n === '') return '—';
  const num = Number(n);
  if (Number.isNaN(num)) return String(n);
  return '$' + num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const pct = (v) => {
  if (v == null) return '—';
  const num = Number(v);
  if (Number.isNaN(num)) return String(v);
  return (num * 100).toFixed(1) + '%';
};

async function fetchJson(apiBase, path, query = '', signal) {
  const url = apiBase + '/dashboard' + path + (query ? '?' + query : '');
  const options = {
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': `Bearer ${userAccessToken()}`,
    },
  };
  if (signal) options.signal = signal;

  const r = await fetch(url, options);
  if (!r.ok) {
    let extra = '';
    try { const t = await r.text(); extra = t ? ' — ' + t.slice(0, 240) : ''; } catch (_) { /* ignore */ }
    throw new Error(r.status + ' ' + r.statusText + extra);
  }
  return r.json();
}

function buildQuery({ year, month, practiceId, pcpId }) {
  const p = { effective_year: year, effective_month: month };
  if (practiceId) p.practice_id = practiceId;
  if (pcpId) p.pcp_id = pcpId;
  return new URLSearchParams(p).toString();
}

function enrichMembers(items) {
  return (items || []).map((it) => ({
    ...it,
    _member:
      [it.member_first_name, it.member_last_name].filter(Boolean).join(' ') ||
      it.member_number ||
      'ID ' + (it.ex_member_id ?? ''),
  }));
}

/* ================================================================
   STATIC CONFIGS (hoisted — zero per-render allocation)
   ================================================================ */

/* Tabulator cell formatters */
const numFormatter = (cell) => fmtNum(cell.getValue(), 3);
const moneyFormatter = (cell) => fmtMoney(cell.getValue());
const pctFormatter = (cell) => pct(cell.getValue());
const moneySorter = (a, b) => (Number(a) || 0) - (Number(b) || 0);

/* react-select custom styles (GitHub Primer aesthetic) */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderRadius: 6,
    border: `1px solid ${state.isFocused ? '#0969da' : '#d0d7de'}`,
    padding: '0 2px',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(9,105,218,.3)' : 'inset 0 1px 0 rgba(208,215,222,.2)',
    minHeight: 32,
    fontSize: '.875rem',
    background: '#fff',
    '&:hover': { borderColor: '#bcc0c5' },
  }),
  menu: (base) => ({
    ...base,
    borderRadius: 6,
    border: '1px solid #d0d7de',
    boxShadow: '0 8px 24px rgba(140,149,159,.2)',
    overflow: 'hidden',
    zIndex: 2000,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  menuList: (base) => ({ ...base, maxHeight: 240, padding: '4px' }),
  option: (base, state) => ({
    ...base,
    padding: '6px 8px',
    fontSize: '.875rem',
    borderRadius: 4,
    background: state.isSelected
      ? '#ddf4ff'
      : state.isFocused
        ? '#f6f8fa'
        : 'transparent',
    color: state.isSelected ? '#0969da' : '#1F2328',
    '&:active': { background: '#b6e3ff' },
  }),
  placeholder: (base) => ({ ...base, color: '#656d76', fontSize: '.875rem' }),
  singleValue: (base) => ({ ...base, color: '#1F2328', fontSize: '.875rem' }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, color: '#656d76', padding: '0 8px', '&:hover': { color: '#1F2328' } }),
  clearIndicator: (base) => ({ ...base, color: '#656d76', padding: '0 4px', '&:hover': { color: '#1F2328' } }),
};

const YEARS = Object.freeze([
    { value: 2026, label: '2026' },
    { value: 2025, label: '2025' },
    { value: 2024, label: '2024' },
    { value: 2023, label: '2023' }
]);

const MONTHS = Object.freeze([
  { value: 0, label: 'All' },
  { value: 1, label: 'Jan' }, { value: 2, label: 'Feb' }, { value: 3, label: 'Mar' },
  { value: 4, label: 'Apr' }, { value: 5, label: 'May' }, { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' }, { value: 8, label: 'Aug' }, { value: 9, label: 'Sep' },
  { value: 10, label: 'Oct' }, { value: 11, label: 'Nov' }, { value: 12, label: 'Dec' },
]);

/* --- Tab definitions --- */
const RAF_TABS = Object.freeze([
  { id: 'dist',     icon: 'bi-bar-chart-line', label: 'Distribution' },
  { id: 'practice', icon: 'bi-buildings',       label: 'By practice' },
  { id: 'pcp',      icon: 'bi-person-badge',    label: 'By PCP' },
]);

const GAPS_TABS = Object.freeze([
  { id: 'practice', icon: 'bi-buildings',        label: 'By practice' },
  { id: 'pcp',      icon: 'bi-person-badge',     label: 'By PCP' },
  { id: 'measure',  icon: 'bi-clipboard-check',  label: 'By measure' },
]);

const MEMBER_TABS = Object.freeze([
  { id: 'risk', icon: 'bi-shield-exclamation', label: 'High risk' },
  { id: 'gaps', icon: 'bi-clipboard2-pulse',   label: 'High gaps' },
  { id: 'cost', icon: 'bi-cash-stack',          label: 'High cost' },
]);

/* --- Search field arrays (stable references) --- */
const SF_PRACTICE = Object.freeze(['practice_name']);
const SF_PCP = Object.freeze(['pcp_name']);
const SF_MEASURE = Object.freeze(['measure_name']);
const SF_DX = Object.freeze(['primary_diagnosis_code']);
const SF_SVC = Object.freeze(['service_code']);
const SF_MEMBER = Object.freeze(['_member', 'practice_name', 'pcp_name']);

/* --- Column definitions --- */
const RAF_PRACTICE_COLS = Object.freeze([
  { title: 'Practice',    field: 'practice_name',    minWidth: 180 },
  { title: 'Members',     field: 'member_count',     hozAlign: 'left', formatter: numFormatter },
  { title: 'With RAF',    field: 'members_with_raf',  hozAlign: 'left', formatter: numFormatter },
  { title: 'Avg current', field: 'avg_raf_current',  hozAlign: 'left', formatter: numFormatter },
  { title: 'Avg new',     field: 'avg_raf_new',      hozAlign: 'left', formatter: numFormatter },
  { title: 'Avg gap',     field: 'avg_raf_gap',      hozAlign: 'left', formatter: numFormatter },
  { title: 'High risk',   field: 'high_risk_count',  hozAlign: 'left', formatter: numFormatter },
]);

const RAF_PCP_COLS = Object.freeze([
  { title: 'PCP',         field: 'pcp_name',         minWidth: 200 },
  { title: 'Members',     field: 'member_count',     hozAlign: 'left', formatter: numFormatter },
  { title: 'With RAF',    field: 'members_with_raf',  hozAlign: 'left', formatter: numFormatter },
  { title: 'Avg current', field: 'avg_raf_current',  hozAlign: 'left', formatter: numFormatter },
  { title: 'Avg new',     field: 'avg_raf_new',      hozAlign: 'left', formatter: numFormatter },
  { title: 'Avg gap',     field: 'avg_raf_gap',      hozAlign: 'left', formatter: numFormatter },
  { title: 'High risk',   field: 'high_risk_count',  hozAlign: 'left', formatter: numFormatter },
]);

const GAPS_PRACTICE_COLS = Object.freeze([
  { title: 'Practice',  field: 'practice_name',  minWidth: 180 },
  { title: 'Total',     field: 'total',          hozAlign: 'left', formatter: numFormatter },
  { title: 'Closed',    field: 'closed',         hozAlign: 'left', formatter: numFormatter },
  { title: 'Open',      field: 'open',           hozAlign: 'left', formatter: numFormatter },
  { title: 'Closure %', field: 'closure_rate',   hozAlign: 'left', formatter: pctFormatter },
]);

const GAPS_PCP_COLS = Object.freeze([
  { title: 'PCP',       field: 'pcp_name',       minWidth: 180 },
  { title: 'Total',     field: 'total',          hozAlign: 'left', formatter: numFormatter },
  { title: 'Closed',    field: 'closed',         hozAlign: 'left', formatter: numFormatter },
  { title: 'Open',      field: 'open',           hozAlign: 'left', formatter: numFormatter },
  { title: 'Closure %', field: 'closure_rate',   hozAlign: 'left', formatter: pctFormatter },
]);

const GAPS_MEASURE_COLS = Object.freeze([
  { title: 'Measure',   field: 'measure_name',   minWidth: 260 },
  { title: 'Total',     field: 'total',          hozAlign: 'left', formatter: numFormatter },
  { title: 'Closed',    field: 'closed',         hozAlign: 'left', formatter: numFormatter },
  { title: 'Open',      field: 'open',           hozAlign: 'left', formatter: numFormatter },
  { title: 'Pending',   field: 'pending',        hozAlign: 'left', formatter: numFormatter },
  { title: 'Closure %', field: 'closure_rate',   hozAlign: 'left', formatter: pctFormatter },
]);

const TOP_DX_COLS = Object.freeze([
  { title: 'Code',       field: 'primary_diagnosis_code', minWidth: 160 },
  { title: 'Line count', field: 'count', hozAlign: 'left', formatter: numFormatter },
]);

const TOP_SVC_COLS = Object.freeze([
  { title: 'Code',       field: 'service_code', minWidth: 160 },
  { title: 'Line count', field: 'count', hozAlign: 'left', formatter: numFormatter },
]);

const HIGH_RISK_COLS = Object.freeze([
  { title: 'Member',      field: '_member',        minWidth: 220 },
  { title: 'Practice',    field: 'practice_name',  minWidth: 170 },
  { title: 'PCP',         field: 'pcp_name',       minWidth: 180 },
  { title: 'RAF current', field: 'raf_current',    hozAlign: 'left', formatter: numFormatter },
  { title: 'RAF new',     field: 'raf_new',        hozAlign: 'left', formatter: numFormatter },
  { title: 'Gap',         field: 'raf_gap',        hozAlign: 'left', formatter: numFormatter },
  { title: 'Open gaps',   field: 'open_gap_count', hozAlign: 'left', formatter: numFormatter },
]);

const HIGH_GAPS_COLS = Object.freeze([
  { title: 'Member',    field: '_member',        minWidth: 220 },
  { title: 'Practice',  field: 'practice_name',  minWidth: 170 },
  { title: 'PCP',       field: 'pcp_name',       minWidth: 180 },
  { title: 'Open gaps', field: 'open_gap_count', hozAlign: 'left', formatter: numFormatter },
  { title: 'RAF new',   field: 'raf_new',        hozAlign: 'left', formatter: numFormatter },
]);

const HIGH_COST_COLS = Object.freeze([
  { title: 'Member',      field: '_member',       minWidth: 220 },
  { title: 'Practice',    field: 'practice_name', minWidth: 170 },
  { title: 'PCP',         field: 'pcp_name',      minWidth: 180 },
  { title: 'Claims cost', field: 'claims_cost',   hozAlign: 'left', formatter: moneyFormatter, sorter: moneySorter },
]);

/* --- Table option presets --- */
const TABLE_OPTS_DEFAULT = Object.freeze({ height: '420px', paginationSize: 10 });
const TABLE_OPTS_TALL = Object.freeze({ height: '520px', paginationSize: 15 });
const TABLE_OPTS_MEASURE = Object.freeze({ height: '520px', paginationSize: 10 });
const TABLE_OPTS_CODE = Object.freeze({ height: '420px', paginationSize: 10 });

/* --- Icon style maps (GitHub Primer accent tones) --- */
const TONE_BG = Object.freeze({
  primary: '#ddf4ff',
  success: '#dafbe1',
  warning: '#fff8c5',
  danger:  '#ffebe9',
});
const TONE_COLOR = Object.freeze({
  primary: '#0969da',
  success: '#1a7f37',
  warning: '#9a6700',
  danger:  '#d1242f',
});

/* ================================================================
   SMALL COMPONENTS (memoized)
   ================================================================ */

const KpiCard = memo(function KpiCard({ label, value, icon = 'bi-activity', tone = 'primary', isMoney = false, isPercent = false, colClass = 'col-6 col-md-4 col-xl-3' }) {
  const spanRef = useRef(null);
  const prevRef = useRef(null);
  const countUpRef = useRef(null);

  const raw = (value == null || value === '') ? null : Number(value);
  const display = (raw == null || Number.isNaN(raw))
    ? '—'
    : isMoney ? fmtMoney(raw) : isPercent ? pct(raw) : fmtNum(raw, 3);

  useEffect(() => {
    if (spanRef.current && raw != null && !Number.isNaN(raw) && raw !== prevRef.current) {
      prevRef.current = raw;
      const target = isPercent ? raw * 100 : raw;
      try {
        countUpRef.current?.reset();
        const cu = new CountUp(spanRef.current, target, {
          duration: 0.9,
          separator: ',',
          decimal: '.',
          decimalPlaces: Number.isInteger(target) ? 0 : 2,
          prefix: isMoney ? '$' : '',
          suffix: isPercent ? '%' : '',
        });
        if (!cu.error) { countUpRef.current = cu; cu.start(); }
      } catch (_) { /* ignore */ }
    }
  }, [raw, isMoney, isPercent]);

  return (
    <div className={colClass}>
      <div className="kpi p-3">
        <div className="d-flex align-items-center justify-content-between position-relative" style={{ zIndex: 1 }}>
          <div className="icon" style={{ background: TONE_BG[tone], color: TONE_COLOR[tone] }}>
            <i className={`bi ${icon} fs-5`} style={{ lineHeight: 1 }} />
          </div>
          <div className="text-end">
            <div className="label">{label}</div>
            <div className="value"><span ref={spanRef}>{display}</span></div>
          </div>
        </div>
      </div>
    </div>
  );
});

const SkeletonKpi = memo(function SkeletonKpi({ colClass = 'col-6 col-md-4 col-xl-3' }) {
  return (
    <div className={colClass}>
      <div className="kpi p-3"><div className="skeleton" style={{ height: 78 }} /></div>
    </div>
  );
});

/* Tabulator wrapper */
const DataTable = memo(function DataTable({
  columns,
  data,
  options = TABLE_OPTS_DEFAULT,
  searchValue,
  searchFields = SF_PRACTICE,
  visible = true,
}) {
  const elRef = useRef(null);
  const tableRef = useRef(null);

  const builtRef = useRef(false);
  const pendingDataRef = useRef(null);
  const columnsRef = useRef(columns);
  const optionsRef = useRef(options);

  // keep refs updated (so init uses latest values)
  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  // ✅ INIT only when visible + element exists + not already initialized
  useEffect(() => {
    if (!visible) return;
    if (!elRef.current) return;
    if (tableRef.current) return;

    builtRef.current = false;

    // ✅ IMPORTANT: set initial data via constructor "data" option (Tabulator recommended)
    const initialData = Array.isArray(data) ? data : [];

    const table = new Tabulator(elRef.current, {
      layout: "fitColumns",
      responsiveLayout: "collapse",
      placeholder: "No data",
      pagination: "local",
      paginationSizeSelector: [10, 20, 50, 100],
      movableColumns: true,
      headerSortTristate: true,
      columnDefaults: { headerTooltip: true },

      columns: columnsRef.current,
      data: initialData,

      ...optionsRef.current,
    });

    tableRef.current = table;

    // ✅ Now it's safe to push updates
    table.on("tableBuilt", () => {
      builtRef.current = true;

      // if data arrived before build finished, apply it now
      if (pendingDataRef.current) {
        const d = pendingDataRef.current;
        pendingDataRef.current = null;

        table
          .replaceData(Array.isArray(d) ? d : [])
          .catch(() => table.setData(Array.isArray(d) ? d : []));
      }

      // ensure proper sizing after build
      requestAnimationFrame(() => table.redraw(true));
    });

    return () => {
      try {
        tableRef.current?.destroy();
      } catch {}
      tableRef.current = null;
      builtRef.current = false;
      pendingDataRef.current = null;
    };
    // NOTE: only depends on visible (we avoid re-init loops)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // ✅ SAFE data updates (never call setData before tableBuilt)
  useEffect(() => {
    if (!data) return;

    const table = tableRef.current;

    // table not created yet -> queue
    if (!table) {
      pendingDataRef.current = data;
      return;
    }

    // table created but not built yet -> queue
    if (!builtRef.current) {
      pendingDataRef.current = data;
      return;
    }

    // safe update
    table
      .replaceData(Array.isArray(data) ? data : [])
      .catch(() => table.setData(Array.isArray(data) ? data : []));
  }, [data]);

  // ✅ Debounced search filter
  useEffect(() => {
    const timer = setTimeout(() => {
      const table = tableRef.current;
      if (!table || !builtRef.current) return;

      if (!searchValue) {
        table.clearFilter(true);
      } else {
        const q = String(searchValue).toLowerCase();
        table.setFilter((row) =>
          searchFields.some((f) => String(row[f] ?? "").toLowerCase().includes(q))
        );
      }
    }, 200);

    return () => clearTimeout(timer);
  }, [searchValue, searchFields]);

  // ✅ redraw when becoming visible
  useEffect(() => {
    const table = tableRef.current;
    if (visible && table && builtRef.current) {
      requestAnimationFrame(() => table.redraw(true));
    }
  }, [visible]);

  return <div ref={elRef} />;
});

// const DataTable = memo(function DataTable({ columns, data, options = TABLE_OPTS_DEFAULT, searchValue, searchFields = SF_PRACTICE, visible = true }) {
//   const elRef = useRef(null);
//   const tableRef = useRef(null);
//   const columnsRef = useRef(columns);
//   const optionsRef = useRef(options);

//   useEffect(() => {
//     if (elRef.current) {
//       tableRef.current = new Tabulator(elRef.current, {
//         layout: 'fitColumns',
//         responsiveLayout: 'collapse',
//         placeholder: 'No data',
//         pagination: 'local',
//         paginationSizeSelector: [10, 20, 50, 100],
//         movableColumns: true,
//         headerSortTristate: true,
//         columnDefaults: { headerTooltip: true },
//         columns: columnsRef.current,
//         data: [],
//         ...optionsRef.current,
//       });
//     }
//     return () => { tableRef.current?.destroy(); tableRef.current = null; };
//   }, []);

//   useEffect(() => {
//     if (tableRef.current && data) {
//       tableRef.current.setData(data);
//     }
//   }, [data]);

//   /* Debounced search filter (200ms) to avoid excessive re-filtering on fast typing */
//   useEffect(() => {
//     const timer = setTimeout(() => {
//       if (!tableRef.current) return;
//       if (!searchValue) {
//         tableRef.current.clearFilter(true);
//       } else {
//         const q = searchValue.toLowerCase();
//         tableRef.current.setFilter((row) =>
//           searchFields.some((f) => String(row[f] ?? '').toLowerCase().includes(q))
//         );
//       }
//     }, 200);
//     return () => clearTimeout(timer);
//   }, [searchValue, searchFields]);

//   useEffect(() => {
//     if (visible && tableRef.current) {
//       setTimeout(() => tableRef.current?.redraw(true), 50);
//     }
//   }, [visible]);

//   return <div ref={elRef} />;
// });

const TabBar = memo(function TabBar({ tabs, activeTab, onChange }) {
  return (
    <ul className="nav nav-pills gap-2" role="tablist">
      {tabs.map((t) => (
        <li key={t.id} className="nav-item" role="presentation">
          <button
            className={`nav-link${activeTab === t.id ? ' active' : ''}`}
            onClick={() => onChange(t.id)}
            type="button"
            role="tab"
            aria-selected={activeTab === t.id}
          >
            <i className={`bi ${t.icon}`} /> {t.label}
          </button>
        </li>
      ))}
    </ul>
  );
});

const SearchInput = memo(function SearchInput({ value, onChange, placeholder }) {
  return (
    <div className="input-group" style={{ maxWidth: 320 }}>
      <span className="input-group-text"><i className="bi bi-search" /></span>
      <input
        className="form-control"
        style={{ border: "1px solid var(--ipa-input)", borderRadius: "0 var(--ipa-radius) var(--ipa-radius) 0" }}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
      />
    </div>
  );
});

/* ================================================================
   LAYOUT COMPONENTS (memoized)
   ================================================================ */

const TopBar = memo(function TopBar({ onToggleFilters }) {
  return (
      <div className="container-xxl">
        <div className="d-flex align-items-center justify-content-end">
          <div className="d-flex align-items-center gap-2">
            <button className="btn btn-outline-primary d-lg-none" onClick={onToggleFilters}>
              <i className="bi bi-sliders2" /> Filters
            </button>
          </div>
        </div>
      </div>
  );
});

const FiltersPanel = memo(function FiltersPanel({ filters, setFilter, practiceOptions, pcpOptions, onLoad }) {
return (
    <div className="glass p-3 p-md-4 mt-3 d-none d-lg-block filters-panel">
        <div className="row g-3 align-items-end">
            <div className="col-6 col-xl-2">
                <label className="form-label small subtle">Effective year</label>
                <select
                    className="form-select"
                    style={{ minHeight: "34.5px" }}
                    value={filters.year}
                    onChange={(e) => setFilter('year', e.target.value)}
                >
                    {YEARS.map((y) => (
                        <option key={y.value} value={y.value}>{y.label}</option>
                    ))}
                </select>
            </div>
            <div className="col-6 col-xl-2">
                <label className="form-label small subtle">Effective month</label>
                <select
                    className="form-select"
                    style={{ minHeight: "34.5px" }}
                    value={filters.month}
                    onChange={(e) => setFilter('month', e.target.value)}
                >
                    {MONTHS.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                    ))}
                </select>
            </div>
            <div className="col-6 col-xl-3">
                <label className="form-label small subtle">Practice</label>
                <Select
                    styles={{
                        ...selectStyles,
                        control: (base, state) => ({
                            ...selectStyles.control(base, state),
                            borderColor: state.isFocused ? 'var(--ipa-ring)' : selectStyles.control(base, state).borderColor,
                            boxShadow: state.isFocused ? 'none' : selectStyles.control(base, state).boxShadow,
                        }),
                    }}
                    options={practiceOptions}
                    value={practiceOptions.find((o) => o.value === filters.practiceId) || null}
                    onChange={(opt) => setFilter('practiceId', opt?.value || '')}
                    isClearable
                    placeholder="All"
                />
            </div>
            <div className="col-6 col-xl-4">
                <label className="form-label small subtle">PCP</label>
                <Select
                    styles={{
                        ...selectStyles,
                        control: (base, state) => ({
                            ...selectStyles.control(base, state),
                            borderColor: state.isFocused ? 'var(--ipa-ring)' : selectStyles.control(base, state).borderColor,
                            boxShadow: state.isFocused ? 'none' : selectStyles.control(base, state).boxShadow,
                        }),
                    }}
                    options={pcpOptions}
                    value={pcpOptions.find((o) => o.value === filters.pcpId) || null}
                    onChange={(opt) => setFilter('pcpId', opt?.value || '')}
                    isClearable
                    placeholder="All"
                />
            </div>
            <div className="col-12 col-xl-1">
                <div className="d-flex align-items-center justify-content-end">
                    <button className="btn btn-primary" onClick={onLoad} style={{ minHeight: "36px", minWidth: "95px" }}>
                        <i className="bi bi-arrow-repeat" /> Filter
                    </button>
                </div>
            </div>
        </div>
        
    </div>
);
});

const MobileFiltersDrawer = memo(function MobileFiltersDrawer({ show, onClose, filters, setFilter, practiceOptions, pcpOptions, onLoad }) {
  /* Close drawer on Escape key */
  useEffect(() => {
    if (!show) return;
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [show, onClose]);

  return (
    <>
      <div
        className={`mobile-drawer-backdrop${show ? ' show' : ''}`}
        onClick={onClose}
        role="button"
        tabIndex={-1}
        aria-label="Close filters"
      />
      <div className={`mobile-drawer${show ? ' show' : ''}`}>
        <div className="p-3 d-flex align-items-center justify-content-between border-bottom">
          <h5 className="brand-title m-0">Filters</h5>
          <button className="btn-close" onClick={onClose} aria-label="Close" />
        </div>
        <div className="p-3">
          <div className="row g-3">
            <div className="col-6">
              <label className="form-label small subtle">Effective year</label>
              <select
                className="form-select"
                style={{ minHeight: "34.5px" }}
                value={filters.year}
                onChange={(e) => setFilter('year', e.target.value)}
              >
                {YEARS.map((y) => (
                  <option key={y.value} value={y.value}>{y.label}</option>
                ))}
              </select>
            </div>
            <div className="col-6">
              <label className="form-label small subtle">Effective month</label>
              <select
                className="form-select"
                value={filters.month}
                onChange={(e) => setFilter('month', e.target.value)}
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>
            <div className="col-12">
              <label className="form-label small subtle">Practice</label>
              <Select
                styles={selectStyles}
                options={practiceOptions}
                value={practiceOptions.find((o) => o.value === filters.practiceId) || null}
                onChange={(opt) => setFilter('practiceId', opt?.value || '')}
                isClearable
                placeholder="All"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="col-12">
              <label className="form-label small subtle">PCP</label>
              <Select
                styles={selectStyles}
                options={pcpOptions}
                value={pcpOptions.find((o) => o.value === filters.pcpId) || null}
                onChange={(opt) => setFilter('pcpId', opt?.value || '')}
                isClearable
                placeholder="All"
                menuPortalTarget={document.body}
              />
            </div>
            <div className="col-12 d-grid">
              <button className="btn btn-primary" onClick={() => { onLoad(); onClose(); }} style={{ minHeight: '36px' }}>
                <i className="bi bi-arrow-repeat" /> Load dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

/* ================================================================
   SECTION: Summary
   ================================================================ */

const SummarySection = memo(function SummarySection({ data }) {
  const loaded = data !== undefined;

  const items = useMemo(() => {
    if (!loaded || data?.error) return null;
    return [
      { label: 'Members',          value: data.member_count,       icon: 'bi-people',               tone: 'primary' },
      { label: 'Practices',        value: data.practice_count,     icon: 'bi-building',             tone: 'primary' },
      { label: 'PCPs',             value: data.pcp_count,          icon: 'bi-person-badge',         tone: 'primary' },
      { label: 'Avg RAF current',  value: data.avg_raf_current,    icon: 'bi-speedometer2',         tone: 'success' },
      { label: 'Avg RAF new',      value: data.avg_raf_new,        icon: 'bi-lightning-charge',     tone: 'success' },
      { label: 'Avg RAF gap',      value: data.avg_raf_gap,        icon: 'bi-arrows-angle-expand',  tone: 'warning' },
      { label: 'High risk count',  value: data.high_risk_count,    icon: 'bi-exclamation-triangle', tone: 'warning' },
      { label: 'Care gaps total',  value: data.care_gaps_total,    icon: 'bi-clipboard2-pulse',     tone: 'primary' },
      { label: 'Care gaps closed', value: data.care_gaps_closed,   icon: 'bi-check2-circle',        tone: 'success' },
      { label: 'Care gaps open',   value: data.care_gaps_open,     icon: 'bi-x-circle',             tone: 'danger' },
      { label: 'Gap closure %',    value: data.gap_closure_rate,   icon: 'bi-percent',              tone: 'success', isPercent: true },
      { label: 'Claims cost',      value: data.claims_total_cost,  icon: 'bi-cash-stack',           tone: 'primary', isMoney: true },
    ];
  }, [data, loaded]);

  if (!loaded) {
    return (
      <section className="mt-4">
        <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-3">
          <div>
            <h2 className="section-title">Summary</h2>
            <div className="subtle">Quick KPIs for the selected period</div>
          </div>
        </div>
        <div className="row g-3">
          {[1, 2, 3, 4].map((i) => <SkeletonKpi key={i} />)}
        </div>
      </section>
    );
  }

  if (data?.error) {
    return (
      <section className="mt-4">
        <h2 className="section-title mb-3">Summary</h2>
        <div className="alert alert-danger glass">Failed to load summary: {data.error}</div>
      </section>
    );
  }

  return (
    <section className="mt-4">
      <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="section-title">Summary</h2>
          <div className="subtle">Quick KPIs for the selected period</div>
        </div>
      </div>
      <div className="row g-3">
        {items.map((it) => <KpiCard key={it.label} {...it} />)}
      </div>
    </section>
  );
});

/* ================================================================
   SECTION: RAF
   ================================================================ */

const RafSection = memo(function RafSection({ rafDist, rafByPractice, rafByPcp }) {
  const [activeTab, setActiveTab] = useState('dist');
  const [practiceSearch, setPracticeSearch] = useState('');
  const [pcpSearch, setPcpSearch] = useState('');
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  /* Render / update chart */
  useEffect(() => {
    chartInstance.current?.destroy();
    chartInstance.current = null;

    if (!chartRef.current || !rafDist || !rafDist.length) return;

    chartInstance.current = new ApexCharts(chartRef.current, {
      chart: { type: 'bar', height: '100%', toolbar: { show: false }, animations: { enabled: true, easing: 'easeinout', speed: 700 }, foreColor: 'rgba(15,23,42,.75)' },
      stroke: { width: 0 },
      grid: { strokeDashArray: 4, padding: { left: 6, right: 10, top: 0, bottom: 0 } },
      series: [{ name: 'Members', data: rafDist.map((b) => Number(b.count || 0)) }],
      xaxis: { categories: rafDist.map((b) => b.bucket ?? ''), labels: { rotate: -35 } },
      plotOptions: { bar: { borderRadius: 2, borderRadiusApplication: 'end', columnWidth: '55%' } },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (v) => fmtNum(v, 0) } },
    });
    chartInstance.current.render();

    return () => { chartInstance.current?.destroy(); chartInstance.current = null; };
  }, [rafDist]);

  /* Redraw chart when switching back to dist tab */
  useEffect(() => {
    if (activeTab === 'dist' && chartInstance.current) {
      setTimeout(() => {
        try { chartInstance.current?.updateOptions({}, false, false); } catch (_) { /* ignore */ }
      }, 80);
    }
  }, [activeTab]);

  return (
    <section className="mt-5">
      <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="section-title">RAF</h2>
          <div className="subtle">Risk adjustment factor insights</div>
        </div>
      </div>

      <div className="glass p-3 p-md-4">
        <TabBar tabs={RAF_TABS} activeTab={activeTab} onChange={setActiveTab} />

        <div className="mt-3">
          <div style={{ display: activeTab === 'dist' ? 'block' : 'none' }}>
            <div className="chart-card relative">
              <div ref={chartRef} className="chart-wrap" style={{ width: '100%', height: '100%' }} />
              {(!rafDist || !rafDist.length) && (
                <div className="text-center subtle small absolute left-0 w-full" style={{ top: "45%" }}>No RAF distribution data.</div>
              )}
            </div>
          </div>

          <div style={{ display: activeTab === 'practice' ? 'block' : 'none' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <div className="subtle small"><i className="bi bi-table me-1" /> Sort, filter, paginate</div>
              <SearchInput value={practiceSearch} onChange={setPracticeSearch} placeholder="Search practice…" />
            </div>
            <DataTable columns={RAF_PRACTICE_COLS} data={rafByPractice} searchValue={practiceSearch} searchFields={SF_PRACTICE} visible={activeTab === 'practice'} />
          </div>

          <div style={{ display: activeTab === 'pcp' ? 'block' : 'none' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <div className="subtle small"><i className="bi bi-table me-1" /> Sort, filter, paginate</div>
              <SearchInput value={pcpSearch} onChange={setPcpSearch} placeholder="Search PCP…" />
            </div>
            <DataTable columns={RAF_PCP_COLS} data={rafByPcp} searchValue={pcpSearch} searchFields={SF_PCP} visible={activeTab === 'pcp'} />
          </div>
        </div>
      </div>
    </section>
  );
});

/* ================================================================
   SECTION: Care Gaps
   ================================================================ */

const CareGapsSection = memo(function CareGapsSection({ gapsSummary, gapsByPractice, gapsByPcp, gapsByMeasure }) {
  const [activeTab, setActiveTab] = useState('practice');
  const [practiceSearch, setPracticeSearch] = useState('');
  const [pcpSearch, setPcpSearch] = useState('');
  const [measureSearch, setMeasureSearch] = useState('');

  const kpiLoaded = gapsSummary !== undefined;
  const kpiError = gapsSummary?.error;

  const gapKpis = useMemo(() => {
    if (kpiError || !kpiLoaded) return null;
    return [
      { label: 'Total',        value: gapsSummary?.total,        icon: 'bi-collection',    tone: 'primary' },
      { label: 'Closed',       value: gapsSummary?.closed,       icon: 'bi-check2-circle', tone: 'success' },
      { label: 'Open',         value: gapsSummary?.open,         icon: 'bi-x-circle',      tone: 'danger' },
      { label: 'Closure rate', value: gapsSummary?.closure_rate, icon: 'bi-percent',        tone: 'success', isPercent: true },
    ];
  }, [gapsSummary, kpiLoaded, kpiError]);

  return (
    <section className="mt-5">
      <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="section-title">Care gaps</h2>
          <div className="subtle">Closure status by practice, PCP, and measure</div>
        </div>
      </div>

      <div className="glass p-3 p-md-4">
        <div className="row g-3 mb-3">
          {!kpiLoaded
            ? [1, 2, 3, 4].map((i) => <SkeletonKpi key={i} colClass="col-6 col-md-3" />)
            : kpiError
              ? <div className="col-12"><div className="alert alert-danger glass mb-0">Failed to load care gaps summary: {kpiError}</div></div>
              : gapKpis.map((it) => <KpiCard key={it.label} {...it} colClass="col-6 col-md-3" />)
          }
        </div>

        <TabBar tabs={GAPS_TABS} activeTab={activeTab} onChange={setActiveTab} />

        <div className="mt-3">
          <div style={{ display: activeTab === 'practice' ? 'block' : 'none' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <div className="subtle small"><i className="bi bi-table me-1" /> Sort, filter, paginate</div>
              <SearchInput value={practiceSearch} onChange={setPracticeSearch} placeholder="Search practice…" />
            </div>
            <DataTable columns={GAPS_PRACTICE_COLS} data={gapsByPractice} searchValue={practiceSearch} searchFields={SF_PRACTICE} visible={activeTab === 'practice'} />
          </div>

          <div style={{ display: activeTab === 'pcp' ? 'block' : 'none' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <div className="subtle small"><i className="bi bi-table me-1" /> Sort, filter, paginate</div>
              <SearchInput value={pcpSearch} onChange={setPcpSearch} placeholder="Search PCP…" />
            </div>
            <DataTable columns={GAPS_PCP_COLS} data={gapsByPcp} searchValue={pcpSearch} searchFields={SF_PCP} visible={activeTab === 'pcp'} />
          </div>

          <div style={{ display: activeTab === 'measure' ? 'block' : 'none' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <div className="subtle small"><i className="bi bi-table me-1" /> Sort, filter, paginate</div>
              <SearchInput value={measureSearch} onChange={setMeasureSearch} placeholder="Search measure…" />
            </div>
            <DataTable columns={GAPS_MEASURE_COLS} data={gapsByMeasure} searchValue={measureSearch} searchFields={SF_MEASURE} visible={activeTab === 'measure'} options={TABLE_OPTS_MEASURE} />
          </div>
        </div>
      </div>
    </section>
  );
});

/* ================================================================
   SECTION: Claims
   ================================================================ */

const ClaimsSection = memo(function ClaimsSection({ claimsSummary, claimsByCategory, topDiag, topSvc }) {
  const [dxSearch, setDxSearch] = useState('');
  const [svcSearch, setSvcSearch] = useState('');
  const chartRef = useRef(null);
  const chartInstance = useRef(null);

  const loaded = claimsSummary !== undefined;
  const error = claimsSummary?.error;

  const kpis = useMemo(() => {
    if (error || !loaded) return null;
    return [
      { label: 'Total cost',   value: claimsSummary?.total_cost,       icon: 'bi-currency-dollar',   tone: 'primary', isMoney: true, colClass: 'col-6 col-md-4 col-xl-2' },
      { label: 'Claim count',  value: claimsSummary?.claim_count,      icon: 'bi-receipt',           tone: 'primary', colClass: 'col-6 col-md-4 col-xl-2' },
      { label: 'Line count',   value: claimsSummary?.line_count,       icon: 'bi-list-check',        tone: 'primary', colClass: 'col-6 col-md-4 col-xl-2' },
      { label: 'Cost/member',  value: claimsSummary?.cost_per_member,  icon: 'bi-person-lines-fill', tone: 'warning', isMoney: true, colClass: 'col-6 col-md-4 col-xl-3' },
      { label: 'PMPM',         value: claimsSummary?.pmpm,             icon: 'bi-calendar3',         tone: 'success', isMoney: true, colClass: 'col-6 col-md-4 col-xl-3' },
    ];
  }, [claimsSummary, loaded, error]);

  /* Chart */
  useEffect(() => {
    chartInstance.current?.destroy();
    chartInstance.current = null;

    if (!chartRef.current || !claimsByCategory || !claimsByCategory.length) return;

    const series = claimsByCategory.map((r) => Number(r.total_cost || 0));
    const labels = claimsByCategory.map((r) => r.category_of_service || 'Unspecified');

    chartInstance.current = new ApexCharts(chartRef.current, {
      chart: { type: 'bar', height: '100%', toolbar: { show: false }, animations: { enabled: true, easing: 'easeinout', speed: 700 }, foreColor: 'rgba(15,23,42,.75)' },
      stroke: { width: 0 },
      grid: { strokeDashArray: 4, padding: { left: 6, right: 10, top: 0, bottom: 0 } },
      series: [{ name: 'Total cost', data: series }],
      xaxis: { categories: labels },
      plotOptions: { bar: { borderRadius: 2, borderRadiusApplication: 'end', horizontal: true, barHeight: '60%' } },
      dataLabels: { enabled: false },
      tooltip: { y: { formatter: (v) => fmtMoney(v) } },
    });
    chartInstance.current.render();

    return () => { chartInstance.current?.destroy(); chartInstance.current = null; };
  }, [claimsByCategory]);

  return (
    <section className="mt-5">
      <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="section-title">Claims</h2>
          <div className="subtle">Cost breakdown and top codes</div>
        </div>
      </div>

      <div className="glass p-3 p-md-4">
        <div className="row g-3 mb-3">
          {!loaded
            ? [1, 2, 3, 4, 5].map((i) => <SkeletonKpi key={i} colClass="col-6 col-md-4 col-xl-2" />)
            : error
              ? <div className="col-12"><div className="alert alert-danger glass mb-0">Failed to load claims summary: {error}</div></div>
              : kpis.map((it) => <KpiCard key={it.label} {...it} />)
          }
        </div>

        <div className="chart-card relative">
          <div ref={chartRef} className="chart-wrap" style={{ width: '100%', height: '100%' }} />
          {(!claimsByCategory || !claimsByCategory.length) && (
            <div className="text-center subtle small absolute left-0 w-full" style={{ top: "45%" }}>No claims by category.</div>
          )}
        </div>

        <div className="row g-4 mt-2">
          <div className="col-12 col-xl-6">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <h3 className="brand-title fs-5 m-0">Top diagnosis codes</h3>
              <SearchInput value={dxSearch} onChange={setDxSearch} placeholder="Search code…" />
            </div>
            <DataTable columns={TOP_DX_COLS} data={topDiag} searchValue={dxSearch} searchFields={SF_DX} options={TABLE_OPTS_CODE} />
          </div>
          <div className="col-12 col-xl-6">
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <h3 className="brand-title fs-5 m-0">Top service codes</h3>
              <SearchInput value={svcSearch} onChange={setSvcSearch} placeholder="Search code…" />
            </div>
            <DataTable columns={TOP_SVC_COLS} data={topSvc} searchValue={svcSearch} searchFields={SF_SVC} options={TABLE_OPTS_CODE} />
          </div>
        </div>
      </div>
    </section>
  );
});

/* ================================================================
   SECTION: Members
   ================================================================ */

const MembersSection = memo(function MembersSection({ highRisk, highGaps, highCost, hasMoreRisk, hasMoreGaps, hasMoreCost, onLoadMore }) {
  const [activeTab, setActiveTab] = useState('risk');
  const [riskSearch, setRiskSearch] = useState('');
  const [gapsSearch, setGapsSearch] = useState('');
  const [costSearch, setCostSearch] = useState('');

  return (
    <section className="mt-5">
      <div className="d-flex align-items-end justify-content-between flex-wrap gap-2 mb-3">
        <div>
          <h2 className="section-title">Members</h2>
          <div className="subtle">High-impact member lists with built-in search</div>
        </div>
      </div>

      <div className="glass p-3 p-md-4">
        <TabBar tabs={MEMBER_TABS} activeTab={activeTab} onChange={setActiveTab} />

        <div className="mt-3">
          <div style={{ display: activeTab === 'risk' ? 'block' : 'none' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <SearchInput value={riskSearch} onChange={setRiskSearch} placeholder="Search members…" />
              {hasMoreRisk && (
                <button className="btn btn-outline-primary" onClick={() => onLoadMore('risk')}>
                  <i className="bi bi-plus-circle" /> Load more
                </button>
              )}
            </div>
            <DataTable columns={HIGH_RISK_COLS} data={highRisk} searchValue={riskSearch} searchFields={SF_MEMBER} options={TABLE_OPTS_TALL} visible={activeTab === 'risk'} />
          </div>

          <div style={{ display: activeTab === 'gaps' ? 'block' : 'none' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <SearchInput value={gapsSearch} onChange={setGapsSearch} placeholder="Search members…" />
              {hasMoreGaps && (
                <button className="btn btn-outline-primary" onClick={() => onLoadMore('gaps')}>
                  <i className="bi bi-plus-circle" /> Load more
                </button>
              )}
            </div>
            <DataTable columns={HIGH_GAPS_COLS} data={highGaps} searchValue={gapsSearch} searchFields={SF_MEMBER} options={TABLE_OPTS_TALL} visible={activeTab === 'gaps'} />
          </div>

          <div style={{ display: activeTab === 'cost' ? 'block' : 'none' }}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
              <SearchInput value={costSearch} onChange={setCostSearch} placeholder="Search members…" />
              {hasMoreCost && (
                <button className="btn btn-outline-primary" onClick={() => onLoadMore('cost')}>
                  <i className="bi bi-plus-circle" /> Load more
                </button>
              )}
            </div>
            <DataTable columns={HIGH_COST_COLS} data={highCost} searchValue={costSearch} searchFields={SF_MEMBER} options={TABLE_OPTS_TALL} visible={activeTab === 'cost'} />
          </div>
        </div>
      </div>
    </section>
  );
});

export default function IPADashboard() {
  const [filters, setFilters] = useState({
    apiBase: Config.apiServerUrl,
    year: 2026,
    month: 0,
    practiceId: '',
    pcpId: '',
  });
  const [practices, setPractices] = useState([]);
  const [pcps, setPcps] = useState([]);
  const [showMobile, setShowMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [dashData, setDashData] = useState(null);
  const [memberData, setMemberData] = useState({
    highRisk: [],
    highGaps: [],
    highCost: [],
    cursorRisk: null,
    cursorGaps: null,
    cursorCost: null,
    hasMoreRisk: false,
    hasMoreGaps: false,
    hasMoreCost: false,
  });

  /* Abort controller ref for cancelling in-flight dashboard loads */
  const abortRef = useRef(null);
  /* Refs for stable callbacks — avoids recreating loadDashboard/loadMore on every filter change */
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const memberDataRef = useRef(memberData);
  memberDataRef.current = memberData;
  const loadingMoreRef = useRef(false);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const apiBase = useMemo(() => filters.apiBase.replace(/\/$/, ''), [filters.apiBase]);

  /* ---- Memoized select options ---- */
  const practiceOptions = useMemo(
    () => practices.map((p) => ({
      value: String(p.id),
      label: p.practice_name || p.provider_tax_id || ('Practice ' + p.id),
    })),
    [practices],
  );

  const pcpOptions = useMemo(
    () => pcps.map((p) => {
      const name = p.pcp_name || '';
      const pcpId = p.pcp_id || '';
      const label = name
        ? (pcpId ? `${name} (ID: ${pcpId})` : name)
        : (pcpId ? `ID: ${pcpId}` : ('PCP ' + p.id));
      return { value: String(p.id), label };
    }),
    [pcps],
  );

  /* ---- Load practice & PCP lists (with AbortController) ---- */
  useEffect(() => {
    if (!apiBase) return;
    loadDashboard();
    const ac = new AbortController();
    (async () => {
      try {
        const data = await fetchJson(apiBase, '/practices', '', ac.signal);
        if (!ac.signal.aborted) setPractices(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (!ac.signal.aborted) notify("error", e.message || e, 0.5);
      }
    })();
    return () => ac.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  useEffect(() => {
    if (!apiBase) return;
    const ac = new AbortController();
    (async () => {
      try {
        const path = filters.practiceId
          ? '/pcps?practice_id=' + encodeURIComponent(filters.practiceId)
          : '/pcps';
        const data = await fetchJson(apiBase, path, '', ac.signal);
        if (!ac.signal.aborted) setPcps(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name === 'AbortError') return;
        if (!ac.signal.aborted) console.warn('PCP list load failed', e);
      }
    })();
    return () => ac.abort();
  }, [apiBase, filters.practiceId]);

  /* ---- Load dashboard (with abort + stable ref-based callback) ---- */
  const loadDashboard = useCallback(async () => {
    if (!apiBase) {
      notify("error", 'Please set API base URL.', 0.5);
      return;
    }

    /* Cancel any in-flight request */
    abortRef.current?.abort();
    const ac = new AbortController();
    abortRef.current = ac;
    const { signal } = ac;

    const q = buildQuery(filtersRef.current);

    setIsLoading(true);

    try {
      const [
        summary, rafByPractice, rafByPcp, rafDist,
        gapsSummary, gapsByPractice, gapsByPcp, gapsByMeasure,
        claimsSummary, claimsByCategory, topDiag, topSvc,
        highRisk, highGaps, highCost,
      ] = await Promise.all([
        fetchJson(apiBase, '/summary', q, signal).catch((e) => ({ error: e.message })),
        fetchJson(apiBase, '/raf/by-practice', q, signal).catch(() => []),
        fetchJson(apiBase, '/raf/by-pcp', q, signal).catch(() => []),
        fetchJson(apiBase, '/raf/distribution', q, signal).then((r) => r.buckets || []).catch(() => []),
        fetchJson(apiBase, '/gaps/summary', q, signal).catch((e) => ({ error: e.message })),
        fetchJson(apiBase, '/gaps/by-practice', q, signal).catch(() => []),
        fetchJson(apiBase, '/gaps/by-pcp', q, signal).catch(() => []),
        fetchJson(apiBase, '/gaps/by-measure', q, signal).catch(() => []),
        fetchJson(apiBase, '/claims/summary', q, signal).catch((e) => ({ error: e.message })),
        fetchJson(apiBase, '/claims/by-category', q, signal).catch(() => []),
        fetchJson(apiBase, '/claims/top-diagnosis', q + '&limit=20', signal).then((r) => (Array.isArray(r) ? r : [])).catch(() => []),
        fetchJson(apiBase, '/claims/top-services', q + '&limit=20', signal).then((r) => (Array.isArray(r) ? r : [])).catch(() => []),
        fetchJson(apiBase, '/members/high-risk', q + '&limit=50', signal).catch((e) => ({ items: [], error: e.message })),
        fetchJson(apiBase, '/members/high-gaps', q + '&limit=50', signal).catch((e) => ({ items: [], error: e.message })),
        fetchJson(apiBase, '/members/high-cost', q + '&limit=50', signal).catch((e) => ({ items: [], error: e.message })),
      ]);

      /* Bail if this request was superseded */
      if (signal.aborted) return;

      setDashData({
        summary,
        rafByPractice: Array.isArray(rafByPractice) ? rafByPractice : [],
        rafByPcp: Array.isArray(rafByPcp) ? rafByPcp : [],
        rafDist,
        gapsSummary,
        gapsByPractice: Array.isArray(gapsByPractice) ? gapsByPractice : [],
        gapsByPcp: Array.isArray(gapsByPcp) ? gapsByPcp : [],
        gapsByMeasure: Array.isArray(gapsByMeasure) ? gapsByMeasure : [],
        claimsSummary,
        claimsByCategory: Array.isArray(claimsByCategory) ? claimsByCategory : [],
        topDiag,
        topSvc,
      });

      setMemberData({
        highRisk: enrichMembers(highRisk.items || []),
        highGaps: enrichMembers(highGaps.items || []),
        highCost: enrichMembers(highCost.items || []),
        cursorRisk: highRisk.next_cursor || null,
        cursorGaps: highGaps.next_cursor || null,
        cursorCost: highCost.next_cursor || null,
        hasMoreRisk: !!highRisk.has_more,
        hasMoreGaps: !!highGaps.has_more,
        hasMoreCost: !!highCost.has_more,
      });

    } catch (err) {
      if (err.name === 'AbortError') return;
      notify("error", err.message || err, 0.5);
    } finally {
      setIsLoading(false);
    }
  }, [apiBase]);

  /* ---- Load more members (guarded against double-clicks, stable callback) ---- */
  const loadMore = useCallback(async (kind) => {
    if (loadingMoreRef.current) return;
    loadingMoreRef.current = true;

    try {
      const q = buildQuery(filtersRef.current) + '&limit=50';
      const cursorData = memberDataRef.current;

      if (kind === 'risk') {
        const cursor = cursorData.cursorRisk;
        const data = await fetchJson(apiBase, '/members/high-risk', q + (cursor ? '&cursor=' + encodeURIComponent(cursor) : '')).catch(() => ({}));
        setMemberData((prev) => ({
          ...prev,
          highRisk: [...prev.highRisk, ...enrichMembers(data.items || [])],
          cursorRisk: data.next_cursor || null,
          hasMoreRisk: !!data.has_more,
        }));
      }
      if (kind === 'gaps') {
        const cursor = cursorData.cursorGaps;
        const data = await fetchJson(apiBase, '/members/high-gaps', q + (cursor ? '&cursor=' + encodeURIComponent(cursor) : '')).catch(() => ({}));
        setMemberData((prev) => ({
          ...prev,
          highGaps: [...prev.highGaps, ...enrichMembers(data.items || [])],
          cursorGaps: data.next_cursor || null,
          hasMoreGaps: !!data.has_more,
        }));
      }
      if (kind === 'cost') {
        const cursor = cursorData.cursorCost;
        const data = await fetchJson(apiBase, '/members/high-cost', q + (cursor ? '&cursor=' + encodeURIComponent(cursor) : '')).catch(() => ({}));
        setMemberData((prev) => ({
          ...prev,
          highCost: [...prev.highCost, ...enrichMembers(data.items || [])],
          cursorCost: data.next_cursor || null,
          hasMoreCost: !!data.has_more,
        }));
      }
    } finally {
      loadingMoreRef.current = false;
    }
  }, [apiBase]);

  /* ---- Stable callbacks ---- */
  const openMobile = useCallback(() => setShowMobile(true), []);
  const closeMobile = useCallback(() => setShowMobile(false), []);

  /* ---- Render ---- */
  return (
    <>
      <TopBar onToggleFilters={openMobile} />

      <main className="container-xxl">
        {/* <StatusAlert message={status.message} kind={status.kind} /> */}

        <FiltersPanel
          filters={filters}
          setFilter={setFilter}
          practiceOptions={practiceOptions}
          pcpOptions={pcpOptions}
          onLoad={loadDashboard}
        />

        <MobileFiltersDrawer
          show={showMobile}
          onClose={closeMobile}
          filters={filters}
          setFilter={setFilter}
          practiceOptions={practiceOptions}
          pcpOptions={pcpOptions}
          onLoad={loadDashboard}
        />

        <SummarySection data={isLoading ? undefined : dashData?.summary} />

        <RafSection
          rafDist={isLoading ? undefined : dashData?.rafDist}
          rafByPractice={isLoading ? undefined : dashData?.rafByPractice}
          rafByPcp={isLoading ? undefined : dashData?.rafByPcp}
        />

        <CareGapsSection
          gapsSummary={isLoading ? undefined : dashData?.gapsSummary}
          gapsByPractice={isLoading ? undefined : dashData?.gapsByPractice}
          gapsByPcp={isLoading ? undefined : dashData?.gapsByPcp}
          gapsByMeasure={isLoading ? undefined : dashData?.gapsByMeasure}
        />

        <ClaimsSection
          claimsSummary={isLoading ? undefined : dashData?.claimsSummary}
          claimsByCategory={isLoading ? undefined : dashData?.claimsByCategory}
          topDiag={isLoading ? undefined : dashData?.topDiag}
          topSvc={isLoading ? undefined : dashData?.topSvc}
        />

        <MembersSection
          highRisk={isLoading ? [] : memberData.highRisk}
          highGaps={isLoading ? [] : memberData.highGaps}
          highCost={isLoading ? [] : memberData.highCost}
          hasMoreRisk={isLoading ? false : memberData.hasMoreRisk}
          hasMoreGaps={isLoading ? false : memberData.hasMoreGaps}
          hasMoreCost={isLoading ? false : memberData.hasMoreCost}
          onLoadMore={loadMore}
        />
      </main>
    </>
  );
}
